import { graphql, useStaticQuery } from "gatsby"
import React, { FC, useMemo, useState } from "react"

import { BespokeComponentFragmentFragment, DelightLabsHeroQueryQuery } from "@graphqlTypes"

import Hero from "@components/Hero/Hero"
import ImageAboveFold from "@components/ImageAboveFold/ImageAboveFold"

import "./DelightLabsHero.scss"
import WistiaPopover from "@components/WistiaPopover/WistiaPopover"
import Button, { BUTTON_SIZES, BUTTON_VARIANTS } from "@components/Button/Button"
import Modal from "@components/Modal/Modal"
import HubspotForm from "@components/HubspotForm/HubspotForm"

type DelightLabsHeroJsonPayload = {
  button1Text?: string,
  button1Url?: string,
  button2Text?: string,
  h1Above: string,
  h1Below?: string,
  wistiaId?: string,
}

const DelightLabsHero: FC<{
  contentful_id: BespokeComponentFragmentFragment["contentful_id"],
  jsonPayload: BespokeComponentFragmentFragment["jsonPayload"],
}> = ({
  contentful_id,
  jsonPayload,
}) => {
    const {
      backgroundImageData,
      backgroundImageGlobeData,
      hubspotForm,
      image1Data,
      image2Data,
      image3Data,
    } = useStaticQuery<DelightLabsHeroQueryQuery>(graphql`
      query DelightLabsHeroQuery {
        backgroundImageData: contentfulAsset(contentful_id:{eq:"6zThUHwq6CGSyYrzl8HMNc"}) {
          imgixImage {
            gatsbyImageData(width: 2000)
          }
        }
        backgroundImageGlobeData: contentfulAsset(contentful_id:{eq:"6kfnqV6lrZuEQ4kRIwQLFG"}) {
          imgixImage {
            gatsbyImageData(width: 2000)
          }
        }
        image1Data: contentfulAsset(contentful_id:{eq:"2w8MDHp4WkdkPBZuaiOKHS"}) {
          imgixImage {
            gatsbyImageData(width: 398)
          }
        }
        image2Data: contentfulAsset(contentful_id:{eq:"1VIwDqKU44E4TndjrcSAvc"}) {
          imgixImage {
            gatsbyImageData(width: 434)
          }
        }
        image3Data: contentfulAsset(contentful_id:{eq:"1nrnACNFbvb8GfTOMclDuC"}) {
          imgixImage {
            gatsbyImageData(width: 383)
          }
        }
        hubspotForm(alternative_id:{eq:"4ae45770-19c8-446b-bf39-72b7aecb720b"}) {
          ...HubspotFormFragment
        }
      }

    `)

    const json = useMemo(() => {
      try {
        return JSON.parse(jsonPayload?.internal.content || "") as DelightLabsHeroJsonPayload
      } catch (e) {
        console.error(`Failed to parse JSON Payload: DelightLabsHero (${contentful_id})`)
        return null
      }
    }, [jsonPayload])

    if (!json) return null

    const [isHubspotModalOpen, setIsHubspotModalOpen] = useState<boolean>(false)

    return (
      <Hero className="DelightLabsHero">
        <div className="background-image-comp">
          {backgroundImageData?.imgixImage?.gatsbyImageData && (
            <ImageAboveFold
              alt=""
              aspectRatio={16 / 10}
              className="background-image"
              imageData={backgroundImageData.imgixImage.gatsbyImageData}
              role="presentation"
            />
          )}
          {backgroundImageGlobeData?.imgixImage?.gatsbyImageData && (
            <ImageAboveFold
              alt=""
              aspectRatio={16 / 10}
              className="background-image-globe"
              imageData={backgroundImageGlobeData.imgixImage.gatsbyImageData}
              role="presentation"
            />
          )}
        </div>
        <div className="content-wrapper">
          <div className="container">
            <div className="grid">
              <h1
                className="column above font--victor-narrow font-size--8XL"
                dangerouslySetInnerHTML={{ __html: json.h1Above }}
              />
              {json.wistiaId && (
                <div className="column">
                  <div className="image-comp">
                    {image1Data?.imgixImage && (
                      <ImageAboveFold
                        alt="Butterfly with clouds"
                        className="image1"
                        aspectRatio={1}
                        imageData={image1Data.imgixImage.gatsbyImageData}
                      />
                    )}
                    {image2Data?.imgixImage && (
                      <ImageAboveFold
                        alt="Cephalospresso"
                        className="image2"
                        aspectRatio={1}
                        imageData={image2Data.imgixImage.gatsbyImageData}
                      />
                    )}
                    {image3Data?.imgixImage && (
                      <ImageAboveFold
                        alt="Pony Crisp"
                        className="image3"
                        aspectRatio={1}
                        imageData={image3Data.imgixImage.gatsbyImageData}
                      />
                    )}
                    <WistiaPopover
                      imageAlt=""
                      id={json.wistiaId}
                    />
                  </div>
                </div>
              )}
              {json.h1Below && (
                <h1
                  className="column below font--victor-narrow font-size--8XL"
                  dangerouslySetInnerHTML={{ __html: json.h1Below }}
                />
              )}
              {(json.button1Text || json.button2Text) && (
                <div className="cta-buttons column">
                  {json.button1Text && (
                    <Button
                      href={json.button1Url}
                      size={BUTTON_SIZES.L}
                    >
                      {json.button1Text}
                    </Button>
                  )}
                  {json.button2Text && (
                    <Button
                      onClick={() => setIsHubspotModalOpen(true)}
                      size={BUTTON_SIZES.L}
                      variant={BUTTON_VARIANTS.SECONDARY}
                    >
                      {json.button2Text}
                    </Button>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
        {isHubspotModalOpen && (
          <Modal
            useInsetCloseButton
            onClose={() => setIsHubspotModalOpen(false)}
          >
            <h4>Keep in touch</h4>
            <HubspotForm {...hubspotForm} />
          </Modal>
        )}
      </Hero>
    )
  }

export default DelightLabsHero
